"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/contexts/auth-context";
import { listLibrary, type LibraryEntryItem } from "@/lib/api/api-client";
import { libraryMinePath } from "@/lib/library/library-routes";
import { EntryCard } from "./entry-card";

/**
 * « Dans mes morceaux » : ce que la recherche du catalogue trouve chez soi.
 *
 * Quatre cartes au plus, une rangée : c'est un raccourci vers ce qu'on a déjà,
 * pas une seconde liste. Au-delà, un lien mène à « Mes morceaux » avec la même
 * recherche, où la liste est complète et paginée.
 *
 * Rien n'est rendu tant qu'il n'y a rien à montrer — ni titre, ni squelette :
 * la plupart des recherches ne trouvent rien chez soi, et un bloc qui paraît
 * puis disparaît ferait sauter les résultats du catalogue juste en dessous.
 * Rien non plus pour un visiteur anonyme, qui n'a pas de morceaux.
 */

const PREVIEW_SIZE = 4;

type Found = {
  readonly query: string;
  readonly entries: readonly LibraryEntryItem[];
  readonly total: number;
};

export function MineMatches({
  query,
  onFound,
}: {
  query: string;
  /** Le nombre de ses morceaux trouvés, 0 tant que rien n'est arrivé. */
  onFound: (total: number) => void;
}) {
  const { isAuthenticated } = useAuth();
  const [found, setFound] = useState<Found | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Deux frappes rapprochées peuvent revenir dans le désordre : seule la
    // réponse de la recherche encore affichée est retenue.
    let current = true;

    listLibrary({ search: query, limit: PREVIEW_SIZE })
      .then((result) => {
        if (!current) {
          return;
        }

        setFound({ query, entries: result.items, total: result.meta.total });
      })
      .catch(() => {
        // Le catalogue répond en dessous : un raccourci manquant ne vaut pas
        // un message d'erreur au-dessus de lui.
        if (current) {
          setFound(null);
        }
      });

    return () => {
      current = false;
    };
  }, [isAuthenticated, query]);

  // Une réponse d'une recherche précédente ne compte plus pour celle-ci.
  const shown = found !== null && found.query === query ? found : null;
  const total = shown?.total ?? 0;

  useEffect(() => {
    onFound(total);
  }, [onFound, total]);

  useEffect(() => {
    return () => onFound(0);
  }, [onFound]);

  if (shown === null || shown.total === 0) {
    return null;
  }

  return (
    <section aria-label="Dans mes morceaux" className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between gap-4 px-1">
        <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-text-secondary">
          Dans mes morceaux
        </h2>
        {shown.total > shown.entries.length && (
          <Link
            href={`${libraryMinePath()}?q=${encodeURIComponent(query)}`}
            className="text-[12px] text-text-soft transition-colors duration-150 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          >
            Voir les {shown.total}
          </Link>
        )}
      </header>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}
      >
        {shown.entries.map((entry) => (
          <EntryCard key={entry.id} entry={entry} showSharedBy={entry.source === "shared"} />
        ))}
      </div>
    </section>
  );
}
